import { z } from 'zod';
import { daysBetweenInclusive } from '../dates';
import { BILLING_TYPES, ITC_STATUSES } from '../enums';
import { morningRevenueRowSchema } from './revenue';
import { morningOtherRowSchema } from './expenses';
import { invoiceFields } from './invoice';
import {
  concurrency,
  emptyAsNull,
  emptyAsUndefined,
  exchangeRate,
  gstRatePercent,
  id,
  listQuery,
  moneyAmount,
  optionalText,
  requiredId,
  ymd,
} from './common';

/** Inclusive date range, start ≤ end, limited in length. */
const dateRange = (maxDays: number) =>
  z
    .object({ startDate: ymd, endDate: ymd })
    .refine((r) => r.startDate <= r.endDate, {
      path: ['endDate'],
      message: 'End date must be on or after start date',
    })
    .refine(
      (r) => r.startDate > r.endDate || daysBetweenInclusive(r.startDate, r.endDate) <= maxDays,
      {
        path: ['endDate'],
        message: `Range cannot exceed ${maxDays} days`,
      },
    );

// ── Marketing expenses ──────────────────────────────────────────────
export const marketingExpenseCreateSchema = z.object({
  transactionDate: ymd,
  /** The app this spend belongs to; the account must be assigned to it on that date. */
  appId: requiredId('App'),
  advertisingAccountId: requiredId('Advertising account'),
  /** Expense before GST. 0 is a valid entry ("checked, no spend"). */
  expenseBeforeGst: moneyAmount,
  /** Omit to use the account's / GST account's default rate. */
  gstRate: emptyAsUndefined(gstRatePercent),
  /** Only for non-INR accounts when no monthly rate is configured. */
  exchangeRate: emptyAsUndefined(exchangeRate),
  ...invoiceFields,
});
export type MarketingExpenseCreateInput = z.output<typeof marketingExpenseCreateSchema>;

/** Date and account identify the daily row and cannot change; delete and re-enter instead. */
export const marketingExpenseUpdateSchema = z
  .object({
    expenseBeforeGst: moneyAmount,
    gstRate: gstRatePercent,
    /** Manual GST to match an invoice; flagged as overridden. */
    gstAmount: emptyAsUndefined(moneyAmount),
    exchangeRate: emptyAsUndefined(exchangeRate),
    ...invoiceFields,
  })
  .partial()
  .extend(concurrency);
export type MarketingExpenseUpdateInput = z.output<typeof marketingExpenseUpdateSchema>;

const marketingFilters = {
  startDate: ymd.optional(),
  endDate: ymd.optional(),
  appId: id.optional(),
  advertisingPlatformId: id.optional(),
  advertisingAccountId: id.optional(),
  gstAccountId: id.optional(),
  managerAccountId: id.optional(),
  billingType: z.enum(BILLING_TYPES).optional(),
  itcStatus: z.enum(ITC_STATUSES).optional(),
};
export const marketingExpenseListSchema = listQuery.extend(marketingFilters);
export const marketingExpenseSummarySchema = z.object(marketingFilters);
export type MarketingFilters = z.output<typeof marketingExpenseSummarySchema>;

export const marketingExpenseBulkSchema = z.object({
  entries: z.array(marketingExpenseCreateSchema).min(1).max(500),
  /** 'error' reports duplicates (account + date already entered); 'overwrite' updates them. */
  onDuplicate: z.enum(['error', 'overwrite']).default('error'),
});

// ── Morning entry ───────────────────────────────────────────────────
export const morningEntryQuerySchema = z.object({ date: ymd.optional() });

export const morningEntrySaveSchema = z.object({
  date: ymd,
  marketing: z
    .array(
      z.object({
        appId: requiredId('App'),
        advertisingAccountId: requiredId('Advertising account'),
        expenseBeforeGst: moneyAmount,
        gstRate: emptyAsUndefined(gstRatePercent),
        /** Id of the entry this row was loaded from (update instead of create). */
        entryId: emptyAsUndefined(id),
        updatedAt: z.iso.datetime({ offset: true }).optional(),
      }),
    )
    .max(500)
    .default([]),
  revenue: z.array(morningRevenueRowSchema).max(500).default([]),
  other: z.array(morningOtherRowSchema).max(200).default([]),
  /** Saved entries the user explicitly removed (soft-deleted, audited). */
  deleteMarketingIds: z.array(id).max(500).default([]),
  deleteRevenueIds: z.array(id).max(500).default([]),
  deleteOtherIds: z.array(id).max(200).default([]),
  /** Update entries that already exist for an account and date instead of failing. */
  overwriteDuplicates: z.boolean().default(false),
});
export type MorningEntrySaveInput = z.output<typeof morningEntrySaveSchema>;

// ── Completeness ────────────────────────────────────────────────────
export const completenessQuerySchema = dateRange(366);

// ── Payments & credit lines ─────────────────────────────────────────
const paymentFields = {
  paymentDate: ymd,
  managerAccountId: emptyAsNull(id),
  advertisingAccountId: emptyAsNull(id),
  amount: moneyAmount.refine((v) => Number(v) > 0, 'Amount must be greater than zero'),
  reference: optionalText(100),
  notes: optionalText(5000),
};
export const advertisingPaymentCreateSchema = z
  .object(paymentFields)
  .refine((p) => !!p.managerAccountId !== !!p.advertisingAccountId, {
    path: ['managerAccountId'],
    message: 'Choose either a manager account (credit line payment) or a prepaid account (top-up)',
  });
export const advertisingPaymentUpdateSchema = z
  .object({
    paymentDate: ymd,
    amount: paymentFields.amount,
    reference: optionalText(100),
    notes: optionalText(5000),
  })
  .partial()
  .extend(concurrency);
export const advertisingPaymentListSchema = listQuery.extend({
  managerAccountId: id.optional(),
  advertisingAccountId: id.optional(),
  startDate: ymd.optional(),
  endDate: ymd.optional(),
});

export const asOfQuerySchema = z.object({ asOf: ymd.optional() });

export const spendSummaryQuerySchema = dateRange(1100);
